import { useEffect, useMemo, useState } from 'react'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { ArrowUpOnSquareIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline'
import { useAuth } from 'context/authContext'
import { hasArticlePermission } from 'constants/permission.utils'
import { CustomTable } from '../../../components/customTable'
import { PagePreloader } from '../../../components/pagePreloader'
import { SmoothTransition } from '../../../components/pageTransition'
import { RenderDefaultActions } from '../common'
import { usePopup } from '../../../context/popupContext'
import { useNotificationBar } from '../../../context/notificationContext'
import { twElementStyles } from '../../../twElementStyles'
import ViewArticleDialog from './ViewArticleDialog'
import ArticleStatusBadge, { ARTICLE_STATUS_LABELS } from './ArticleStatusBadge'

import { getUpdatedSearchParams, parseSearchParams } from '../../../services/util'
import { ArticlePayload, useDeleteArticle, useGetArticleData, usePublishArticle } from 'hooks/article'
import { ConflictDetailsDrawer } from 'components/ConflictDetailsDrawer'

const ArticleList = () => {
    const [openView, setOpenView] = useState(false)
    const [viewData, setViewData] = useState<any>(null)
    const [deleteConflict, setDeleteConflict] = useState<{
        message: string
        details: string[]
    } | null>(null)
    const [publishError, setPublishError] = useState<string | null>(null)

    const navigate = useNavigate()
    const permissions = useAuth()?.user?.permissions
    const canUpdate = hasArticlePermission(permissions, 'update')
    const canDelete = hasArticlePermission(permissions, 'delete')
    const canReview = hasArticlePermission(permissions, 'review')
    const canPublish = hasArticlePermission(permissions, 'publish')
    const { mutate: publishArticle, isLoading: publishing } = usePublishArticle()
    const popup = usePopup()
    const notification = useNotificationBar()

    // kept for the table component's selection contract (no row selection is used here)
    const rowSelectionState = useState<RowSelectionState>({})

    const { mutate: deleteArticle } = useDeleteArticle()

    // filter / search / pagination state lives in the URL
    const [searchParams, setSearchParams] = useSearchParams()
    const filterParams: any = parseSearchParams(searchParams, 'filter')

    const { data, isLoading, refetch, isError, dataUpdatedAt } = useGetArticleData(filterParams, {
        keepPreviousData: true,
    } as any)

    const useFormHook = useForm({ defaultValues: filterParams })

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (!name) return
            const updated = getUpdatedSearchParams(filterParams, values, name)
            if (Object.keys(updated)?.length) {
                setSearchParams({ filter: encodeURIComponent(JSON.stringify(updated)) })
            } else {
                setSearchParams({})
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const responseData = useMemo(() => {
        if (!data?.data) return null
        return {
            ...data.data,
            results: Array.isArray(data.data.results) ? data.data.results : [],
        }
    }, [data])

    /* ------------------------------ ACTIONS ------------------------------ */

    const onDeleteAction = (row: any) => {
        popup.open({
            title: 'Are you sure ?',
            message: (
                <>
                    Do you want to delete <strong>{row?.title}</strong>? This cannot be undone.
                </>
            ),
            type: 'confirm',
            onAction: (confirmed: boolean) => {
                if (!confirmed) return
                deleteArticle(
                    { id: row?.id },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: 'Article deleted',
                                message: `${row?.title} has been deleted successfully.`,
                                type: 'info',
                            })
                            refetch()
                        },
                        onError: (error: any) => {
                            const response = error?.response?.data || {}
                            const message = response?.message || 'Something went wrong'
                            const details = Array.isArray(response?.details) ? response.details : []

                            if (error?.response?.status === 409 || response?.statusCode === 409) {
                                notification.push({
                                    title: 'Cannot Delete',
                                    message: (
                                        <div className="space-y-2">
                                            <p className="text-xs leading-snug text-slate-700 dark:text-slate-200">
                                                {message}
                                            </p>
                                            <button
                                                type="button"
                                                className="text-xs font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                onClick={() => setDeleteConflict({ message, details })}
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    ),
                                    type: 'error',
                                })
                                return
                            }

                            notification.push({ title: 'Error', message, type: 'error' })
                        },
                    }
                )
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed,
        })
    }

    const publishRow = (row: any) => {
        const data: Partial<ArticlePayload> = {
            title: row.title,
            slug: row.slug,
            short_description: row.short_description || '',
            author: row.author || '',
            expected_version: row.version,
        }
        if (row.content_html) data.content_html = row.content_html
        if (row.seo_title !== undefined) data.seo_title = row.seo_title
        if (row.meta_description !== undefined) data.meta_description = row.meta_description
        publishArticle(
            { id: row.id, data },
            {
                onSuccess: () => {
                    setPublishError(null)
                    notification.push({
                        title: 'Article published',
                        message: `${row.title} is now published.`,
                        type: 'success',
                    })
                    setOpenView(false)
                    refetch()
                },
                onError: (error: any) => {
                    const raw = error?.response?.data?.message || 'Something went wrong'
                    const message = /seo_title and meta_description/i.test(raw)
                        ? 'Add an SEO title and meta description before publishing.'
                        : raw
                    setPublishError(message)
                    notification.push({
                        title: 'Could not publish',
                        message,
                        type: 'error',
                    })
                },
            }
        )
    }

    const onAction = (actionType: string, row: any) => {
        if (actionType === 'edit' || actionType === 'review') navigate(`/articles/edit/${row.id}`)
        if (actionType === 'delete') onDeleteAction(row)
        if (actionType === 'view') {
            const fullRow = responseData?.results?.find((item: any) => item.id === row.id)
            setViewData(fullRow || row || null)
            setOpenView(true)
        }
    }

    /* ------------------------------ COLUMNS ------------------------------ */

    const dateCell = ({ getValue }: any) => {
        const value = getValue() as string | null
        return value ? moment(value).format('DD MMM YYYY') : '-'
    }

    const pageIndex = Number(responseData?.pagination?.pageIndex ?? filterParams?.pagination?.pageIndex ?? 0) || 0
    const pageSize = Number(responseData?.pagination?.pageSize ?? filterParams?.pagination?.pageSize ?? 10) || 10

    const columns: ColumnDef<any>[] = [
        {
            id: 'sr',
            header: 'SR.',
            cell: ({ row }) => pageIndex * pageSize + row.index + 1,
            enableSorting: false,
        },
        {
            accessorKey: 'title',
            header: 'TITLE',
            cell: ({ row }) => (
                <div className="max-w-[260px]">
                    <div className="truncate font-medium" title={row.original.title}>
                        {row.original.title}
                    </div>
                    {row.original.slug && (
                        <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                            /{row.original.slug}
                        </div>
                    )}
                </div>
            ),
            enableSorting: false,
        },
        { accessorKey: 'author', header: 'AUTHOR', enableSorting: false },
        {
            accessorKey: 'short_description',
            header: 'SHORT DESCRIPTION',
            cell: ({ getValue }) => {
                const text = String(getValue() || '')
                return text ? (
                    <div className="w-[240px] truncate" title={text}>
                        {text}
                    </div>
                ) : (
                    '-'
                )
            },
            enableSorting: false,
        },
        {
            id: 'status',
            header: 'STATUS',
            accessorFn: (row) => row.status,
            cell: ({ row }) => <ArticleStatusBadge status={row.original.status} />,
            enableSorting: false,
        },
        { accessorKey: 'created_at', header: 'CREATED DATE', cell: dateCell, enableSorting: false },
        { accessorKey: 'updated_at', header: 'LAST UPDATED', cell: dateCell },
        { accessorKey: 'published_at', header: 'PUBLISHED DATE', cell: dateCell, enableSorting: false },
        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) => {
                const status = row.original.status
                const editable = ['draft', 'changes_requested', 'approved', 'published', 'archived'].includes(status)
                const actions: Array<'edit' | 'delete' | 'view'> = ['view']
                if (canUpdate && editable) actions.push('edit')
                if (canDelete) actions.push('delete')
                return (
                    <div className="flex items-center">
                        {RenderDefaultActions(row, actions, onAction)}
                        {canReview && status === 'in_review' && (
                            <button
                                title="Review"
                                className="px-1 py-1 text-lg hover:opacity-60"
                                onClick={() => onAction('review', row.original)}
                            >
                                <ClipboardDocumentCheckIcon className="h-4 w-4 text-blue-800 dark:text-blue-500" />
                            </button>
                        )}
                        {canPublish && status === 'approved' && (
                            <button
                                title="Publish"
                                className="px-1 py-1 text-lg hover:opacity-60 disabled:opacity-40"
                                disabled={publishing}
                                onClick={() => publishRow(row.original)}
                            >
                                <ArrowUpOnSquareIcon className="h-4 w-4 text-blue-800 dark:text-blue-500" />
                            </button>
                        )}
                    </div>
                )
            },
        },
    ]

    const filterOptions = [
        {
            key: 'status',
            label: 'Status',
            options: [
                { display: 'All', value: '' },
                { display: ARTICLE_STATUS_LABELS.draft, value: 'draft' },
                { display: ARTICLE_STATUS_LABELS.in_review, value: 'in_review' },
                { display: ARTICLE_STATUS_LABELS.changes_requested, value: 'changes_requested' },
                { display: ARTICLE_STATUS_LABELS.approved, value: 'approved' },
                { display: ARTICLE_STATUS_LABELS.published, value: 'published' },
                { display: ARTICLE_STATUS_LABELS.archived, value: 'archived' },
            ],
        },
    ]

    if (isLoading) return <PagePreloader />

    return (
        <SmoothTransition>
            <div className="w-full rounded-lg bg-white px-6 pt-2 shadow dark:bg-slate-800">
                <ViewArticleDialog
                    isOpen={openView}
                    setIsOpen={setOpenView}
                    articleData={viewData}
                    canPublish={canPublish}
                    publishing={publishing}
                    publishError={publishError}
                    onPublish={(article) => publishRow(article)}
                />

                <ConflictDetailsDrawer
                    isOpen={!!deleteConflict}
                    onClose={() => setDeleteConflict(null)}
                    title="Cannot Delete Article"
                    description={deleteConflict?.message ?? ''}
                    details={deleteConflict?.details ?? []}
                />

                <CustomTable
                    title="Articles"
                    titleLabel={`Last updated ${moment(dataUpdatedAt).fromNow()}`}
                    columns={columns}
                    data={responseData?.results}
                    pages={responseData?.pages}
                    totalRecords={responseData?.total}
                    isLoading={isLoading}
                    isErrored={isError}
                    formHook={useFormHook}
                    rowSelectionState={rowSelectionState}
                    filterOptions={filterOptions}
                    onRefresh={refetch}
                    onAddAction={() => navigate('/articles/create')}
                />
            </div>
        </SmoothTransition>
    )
}

export default ArticleList